/**
 * Mock proposals data layer.
 *
 * The store is shaped like the API `ProposalDetail` DTO (nested `artist`/
 * `project` refs, `coverMedia`, snake_case status). Exported functions map DTOs
 * to the CMS view model.
 */

import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince, relativeTime } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import type { MediaRef } from './media';
import { type Paginated, delay } from './mock';
import { pushActivity } from './notifications';

export type ProposalStatus = 'In review' | 'Shortlisted' | 'Winner' | 'Not selected';
export type ProposalApiStatus = 'in_review' | 'shortlisted' | 'winner' | 'not_selected';
export type ProposalOrderKey = 'newest' | 'title' | 'votes-desc' | 'votes-asc';

export const PROPOSAL_STATUS_LABEL: Record<ProposalApiStatus, ProposalStatus> = {
  in_review: 'In review',
  shortlisted: 'Shortlisted',
  winner: 'Winner',
  not_selected: 'Not selected',
};

export const PROPOSAL_STATUS_API: Record<ProposalStatus, ProposalApiStatus> = {
  'In review': 'in_review',
  Shortlisted: 'shortlisted',
  Winner: 'winner',
  'Not selected': 'not_selected',
};

export interface EntityRef {
  id: string;
  name: string;
  slug: string;
}

/** API shape returned by `GET /admin/proposals`. */
export interface ProposalDto {
  id: string;
  createdAt: string;
  updatedAt: string;
  projectId: string;
  artistId: string;
  title: string;
  slug: string;
  description: string | null;
  status: ProposalApiStatus;
  votesCount: number;
  artist: EntityRef | null;
  project: EntityRef | null;
  /** Cover + gallery resolved by the API. */
  media?: { cover: MediaRef | null; gallery: MediaRef[] };
  archivedAt?: string | null;
}

export interface Proposal {
  id: string;
  slug: string;
  title: string;
  description: string;
  artistId: string;
  artist: string;
  artistSlug: string;
  projectId: string;
  project: string;
  projectSlug: string;
  status: ProposalStatus;
  votes: number;
  coverMedia?: MediaRef | null;
  gallery?: MediaRef[];
  updated: string;
  updatedHours: number;
  archived: boolean;
}

export interface ProposalInput {
  title: string;
  projectId?: string;
  project: string;
  artistId?: string;
  artist: string;
  status: string;
  description: string;
  coverMedia?: MediaRef | null;
  gallery?: MediaRef[];
}

export interface ListProposalsParams {
  search?: string;
  status?: ProposalStatus | 'All';
  project?: string;
  artistSlug?: string;
  order?: ProposalOrderKey;
  page?: number;
  pageSize?: number;
}

export interface PaginatedProposals {
  items: Proposal[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const PROPOSAL_PAGE_SIZE = 5;
export const PROPOSAL_PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'proposal'
  );
}

function ref(name: string): EntityRef {
  const slug = slugify(name);
  return { id: slug, name, slug };
}

function isoHoursAgo(hours: number): string {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

interface Seed {
  id: string;
  title: string;
  description: string;
  artist: string;
  project: string;
  status: ProposalApiStatus;
  votesCount: number;
  updatedHours: number;
  coverMedia?: MediaRef;
  gallery?: MediaRef[];
}

const SEED: Seed[] = [
  {
    id: 'still-water',
    title: 'Still Water',
    description: 'A quiet study of reflection and surface tension.',
    artist: 'Mara Oliveira',
    project: 'Bloom',
    status: 'winner',
    votesCount: 612,
    updatedHours: 2,
    coverMedia: {
      id: 'still-water-01',
      url: 'https://cdn.needfulthings.com/media/still-water-01.jpg',
    },
    gallery: [{ id: 'quarry-light', url: 'https://cdn.needfulthings.com/media/quarry-light.jpg' }],
  },
  {
    id: 'quarry-light',
    title: 'Quarry Light',
    description: 'Light cutting through carved stone.',
    artist: 'Tomas Beck',
    project: 'Bloom',
    status: 'shortlisted',
    votesCount: 384,
    updatedHours: 6,
  },
  {
    id: 'paper-arches',
    title: 'Paper Arches',
    description: 'Folded paper forms echoing architectural arches.',
    artist: 'Priya Nair',
    project: 'Bloom',
    status: 'shortlisted',
    votesCount: 271,
    updatedHours: 20,
  },
  {
    id: 'salt-lines',
    title: 'Salt Lines',
    description: 'Corroded metal scored by salt and time.',
    artist: 'Ade Bakare',
    project: 'Salt & Iron',
    status: 'winner',
    votesCount: 540,
    updatedHours: 5,
  },
  {
    id: 'anvil',
    title: 'Anvil',
    description: 'A forged response to weight and resistance.',
    artist: 'Jonas Feld',
    project: 'Salt & Iron',
    status: 'in_review',
    votesCount: 0,
    updatedHours: 30,
  },
  {
    id: 'ember-study',
    title: 'Ember Study',
    description: 'Charcoal and pigment tracing the aftermath of fire.',
    artist: 'Lena Costa',
    project: 'Ember Field',
    status: 'shortlisted',
    votesCount: 233,
    updatedHours: 40,
  },
  {
    id: 'ash-field',
    title: 'Ash Field',
    description: 'A scorched field rendered in layered ash.',
    artist: 'Rui Tanaka',
    project: 'Ember Field',
    status: 'not_selected',
    votesCount: 118,
    updatedHours: 60,
  },
  {
    id: 'cold-cast',
    title: 'Cold Cast',
    description: 'Concrete forms cast against winter light.',
    artist: 'Sofia Marchetti',
    project: 'Concrete Seasons',
    status: 'in_review',
    votesCount: 0,
    updatedHours: 90,
  },
  {
    id: 'tidal-mark',
    title: 'Tidal Mark',
    description: 'Impressions left on the shore as water retreats.',
    artist: 'Mara Oliveira',
    project: 'Tidal',
    status: 'winner',
    votesCount: 421,
    updatedHours: 120,
  },
  {
    id: 'paper-moonrise',
    title: 'Paper Moonrise',
    description: 'A cut-paper lunar horizon.',
    artist: 'Priya Nair',
    project: 'Paper Moon',
    status: 'shortlisted',
    votesCount: 190,
    updatedHours: 150,
  },
  {
    id: 'verdant-lines',
    title: 'Verdant Lines',
    description: 'Botanical forms drawn in continuous line.',
    artist: 'Lena Costa',
    project: 'Verdant',
    status: 'winner',
    votesCount: 388,
    updatedHours: 200,
  },
  {
    id: 'static-field',
    title: 'Static Field',
    description: 'Digital noise resolving into flora.',
    artist: 'Rui Tanaka',
    project: 'Static Bloom',
    status: 'in_review',
    votesCount: 0,
    updatedHours: 260,
  },
];

const artistRef = (seed: Seed) => ref(seed.artist);
const projectRef = (seed: Seed) => ref(seed.project);

let db: ProposalDto[] = SEED.map((seed) => {
  const artist = artistRef(seed);
  const project = projectRef(seed);
  return {
    id: seed.id,
    createdAt: isoHoursAgo(seed.updatedHours + 24),
    updatedAt: isoHoursAgo(seed.updatedHours),
    projectId: project.id,
    artistId: artist.id,
    title: seed.title,
    slug: seed.id,
    description: seed.description,
    status: seed.status,
    votesCount: seed.votesCount,
    artist,
    project,
    media: { cover: seed.coverMedia ?? null, gallery: seed.gallery ?? [] },
  };
});

/** Maps an API proposal DTO to the CMS view model. */
export function mapProposal(dto: ProposalDto): Proposal {
  const cover = dto.media?.cover ?? null;
  return {
    id: dto.id,
    slug: dto.slug ?? dto.id,
    title: dto.title,
    description: dto.description ?? '',
    artistId: dto.artistId,
    artist: dto.artist?.name ?? '',
    artistSlug: dto.artist?.slug ?? '',
    projectId: dto.projectId,
    project: dto.project?.name ?? '',
    projectSlug: dto.project?.slug ?? '',
    status: PROPOSAL_STATUS_LABEL[dto.status],
    votes: dto.votesCount,
    coverMedia: cover,
    gallery: dto.media?.gallery ?? [],
    updated: relativeTime(dto.updatedAt),
    updatedHours: hoursSince(dto.updatedAt),
    archived: Boolean(dto.archivedAt),
  };
}

function sortProposals(items: ProposalDto[], order: ProposalOrderKey): ProposalDto[] {
  const sorted = [...items];
  switch (order) {
    case 'title':
      sorted.sort((a, b) => a.title.localeCompare(b.title));
      break;
    case 'votes-desc':
      sorted.sort((a, b) => b.votesCount - a.votesCount);
      break;
    case 'votes-asc':
      sorted.sort((a, b) => a.votesCount - b.votesCount);
      break;
    default:
      sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }
  return sorted;
}

function paginate(items: ProposalDto[], page: number, pageSize: number): PaginatedProposals {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;

  return {
    items: items.slice(start, start + pageSize).map(mapProposal),
    total,
    page: safePage,
    pageSize,
    totalPages,
  };
}

function applyFilters(
  items: ProposalDto[],
  status: ProposalStatus | 'All',
  project?: string,
  artistSlug?: string,
): ProposalDto[] {
  return items.filter((proposal) => {
    const matchesStatus = status === 'All' || mapProposal(proposal).status === status;
    const matchesProject = !project || proposal.project?.slug === project;
    const matchesArtist = !artistSlug || proposal.artist?.slug === artistSlug;
    return matchesStatus && matchesProject && matchesArtist;
  });
}

/** Fetches a page of proposals from the API and maps it to the view model. */
async function fetchProposals(params: ListProposalsParams): Promise<PaginatedProposals> {
  const response = await sdk.api.admin.proposals.get({
    query: {
      search: params.search,
      status:
        params.status && params.status !== 'All' ? PROPOSAL_STATUS_API[params.status] : undefined,
      projectId: params.project,
      artistId: params.artistSlug,
      order:
        params.order === 'title'
          ? 'title'
          : params.order === 'votes-desc' || params.order === 'votes-asc'
            ? 'votes'
            : 'newest',
      page: params.page,
      pageSize: params.pageSize,
    },
  });
  const page = unwrap<Paginated<ProposalDto>>(response);
  return { ...page, items: page.items.map(mapProposal) };
}

export async function listProposals(params: ListProposalsParams = {}): Promise<PaginatedProposals> {
  if (!USE_MOCK) return fetchProposals(params);
  await delay();
  const {
    status = 'All',
    project,
    artistSlug,
    order = 'newest',
    page = 1,
    pageSize = PROPOSAL_PAGE_SIZE,
  } = params;

  return paginate(
    sortProposals(applyFilters(db, status, project, artistSlug), order),
    page,
    pageSize,
  );
}

export async function searchProposals(
  term: string,
  params: ListProposalsParams = {},
): Promise<PaginatedProposals> {
  if (!USE_MOCK) return fetchProposals({ ...params, search: term });
  await delay();
  const {
    status = 'All',
    project,
    artistSlug,
    order = 'newest',
    page = 1,
    pageSize = PROPOSAL_PAGE_SIZE,
  } = params;

  const query = term.trim().toLowerCase();
  const results = db.filter(
    (proposal) =>
      proposal.title.toLowerCase().includes(query) ||
      (proposal.artist?.name ?? '').toLowerCase().includes(query),
  );

  return paginate(
    sortProposals(applyFilters(results, status, project, artistSlug), order),
    page,
    pageSize,
  );
}

export function queryProposals(params: ListProposalsParams = {}): Promise<PaginatedProposals> {
  if (!USE_MOCK) return fetchProposals(params);
  const { search, ...rest } = params;
  return search?.trim() ? searchProposals(search, rest) : listProposals(rest);
}

/** Persists a proposal's cover/gallery usages via `PUT /:id/media`. */
async function saveProposalMedia(
  id: string,
  cover: MediaRef | null | undefined,
  gallery: MediaRef[] | undefined,
): Promise<void> {
  await sdk.api.admin.proposals({ id }).media.put({
    coverMediaId: cover?.id ?? null,
    galleryMediaIds: (gallery ?? []).map((item) => item.id),
  });
}

export async function getProposal(slug: string): Promise<Proposal | undefined> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.proposals({ id: slug }).get();
    return mapProposal(unwrap<ProposalDto>(response));
  }
  await delay();
  const dto = db.find((proposal) => proposal.slug === slug || proposal.id === slug);
  return dto ? mapProposal(dto) : undefined;
}

export async function createProposal(input: ProposalInput): Promise<Proposal> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.proposals.post({
      projectId: input.projectId ?? '',
      artistId: input.artistId ?? '',
      title: input.title,
      description: input.description,
      status: PROPOSAL_STATUS_API[input.status as ProposalStatus] ?? 'in_review',
    });
    const created = unwrap<{ id: string }>(response);
    await saveProposalMedia(created.id, input.coverMedia, input.gallery);
    const detail = await sdk.api.admin.proposals({ id: created.id }).get();
    return mapProposal(unwrap<ProposalDto>(detail));
  }
  await delay();

  let slug = slugify(input.title);
  while (db.some((proposal) => proposal.slug === slug)) {
    slug = `${slug}-1`;
  }

  const artist = ref(input.artist);
  const project = ref(input.project);
  const now = new Date().toISOString();
  const dto: ProposalDto = {
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    projectId: project.id,
    artistId: artist.id,
    title: input.title,
    slug,
    description: input.description,
    status: PROPOSAL_STATUS_API[input.status as ProposalStatus] ?? 'in_review',
    votesCount: 0,
    artist,
    project,
    media: { cover: input.coverMedia ?? null, gallery: input.gallery ?? [] },
  };

  db = [dto, ...db];
  await pushActivity({
    verb: 'created',
    entity: 'proposal',
    targetType: 'proposal',
    targetSlug: dto.slug,
    targetLabel: dto.title,
  });
  return mapProposal(dto);
}

export async function updateProposal(id: string, input: ProposalInput): Promise<Proposal> {
  if (!USE_MOCK) {
    await sdk.api.admin.proposals({ id }).patch({
      title: input.title,
      description: input.description,
      artistId: input.artistId,
      projectId: input.projectId,
      status: PROPOSAL_STATUS_API[input.status as ProposalStatus],
    });
    await saveProposalMedia(id, input.coverMedia, input.gallery);
    const detail = await sdk.api.admin.proposals({ id }).get();
    return mapProposal(unwrap<ProposalDto>(detail));
  }
  await delay();
  let updated: ProposalDto | undefined;

  const artist = ref(input.artist);
  const project = ref(input.project);

  db = db.map((proposal) => {
    if (proposal.id !== id) return proposal;
    updated = {
      ...proposal,
      title: input.title,
      description: input.description,
      artist,
      artistId: artist.id,
      project,
      projectId: project.id,
      status: PROPOSAL_STATUS_API[input.status as ProposalStatus] ?? proposal.status,
      media: { cover: input.coverMedia ?? null, gallery: input.gallery ?? [] },
      updatedAt: new Date().toISOString(),
    };
    return updated;
  });

  if (!updated) throw new Error(`Proposal ${id} not found`);
  await pushActivity({
    verb: 'updated',
    entity: 'proposal',
    targetType: 'proposal',
    targetSlug: updated.slug,
    targetLabel: updated.title,
  });
  return mapProposal(updated);
}

export async function archiveProposal(id: string): Promise<Proposal> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.proposals({ id }).archive.post();
    return mapProposal(unwrap<ProposalDto>(response));
  }
  return setArchived(id, new Date().toISOString());
}

export async function restoreProposal(id: string): Promise<Proposal> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.proposals({ id }).restore.post();
    return mapProposal(unwrap<ProposalDto>(response));
  }
  return setArchived(id, null);
}

async function setArchived(id: string, archivedAt: string | null): Promise<Proposal> {
  await delay();
  let updated: ProposalDto | undefined;
  db = db.map((proposal) => {
    if (proposal.id !== id) return proposal;
    updated = { ...proposal, archivedAt, updatedAt: new Date().toISOString() };
    return updated;
  });
  if (!updated) throw new Error(`Proposal ${id} not found`);
  await pushActivity({
    verb: archivedAt ? 'archived' : 'restored',
    entity: 'proposal',
    targetType: 'proposal',
    targetSlug: updated.slug,
    targetLabel: updated.title,
  });
  return mapProposal(updated);
}
